import React, { useState, useEffect, useRef } from "react";
import { StyleSheet } from 'react-native';
import {
    Box,
    Grid,
    AccordionPanel
} from "grommet";
import { getReport, getSwimmingPoolInfo } from "../api";
import { COLOUR_MAPS, SWIMMING_POOL_PROPERTY_TYPES } from '../constants';
import { PrintHideBox, GapBox, PrimaryButton } from "../components";
import {
    SearchSection,
    LinksSection,
    AddressSection,
    ZoningSection,
    GallerySection,
    InfoSection
} from './';
import { processImageList } from "../utils"
import { useAuth } from "../context";

const SecuritySection = ({
    id,
    index,
    isMobile,
    panel,
    panels,
    updatePanel,
    deletePanel
}) => {
    const [addressSelected, setAddressSelected] = useState('');
    const [addressParsed, setAddressParsed] = useState({});
    const [gnafSelected, setGnafSelected] = useState('');
    const [propertyTypeSelected, setPropertyTypeSelected] = useState('');

    const [reportVisibility, setReportVisibility] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState();
    const [mappedData, setMappedData] = useState();
    const [imageList, setImageList] = useState([]);
    const [swimmingPoolData, setSwimmingPoolData] = useState();

    const [dealAcceptance, setDealAcceptance] = useState();
    const [pricingData, setPricingData] = useState({});
    const hasFetchedRef = useRef(false);
    const { tokens } = useAuth();
    
    const updatePanelValues = (newValues) => {
        updatePanel(id, newValues);
    };

    const panelValues = panel.values || {};

    useEffect(() => {
        // Initialize component state from panel values if they exist
        if (panelValues?.storage) {
            const storage = panelValues?.storage;
            if (storage?.gnafSelected) setGnafSelected(storage?.gnafSelected);
            if (storage?.propertyTypeSelected) setPropertyTypeSelected(storage?.propertyTypeSelected);
            if (storage?.addressSelected) setAddressSelected(storage?.addressSelected);
            if (storage?.addressParsed) setAddressParsed(storage?.addressParsed);
            if (storage?.reportData) setReportData(storage?.reportData);
            if (storage?.hasFetchedRef) hasFetchedRef.current = storage?.hasFetchedRef;

            // Any other values that need to be initialized
        }
    }, []);

    // query report
    useEffect(() => {
        const shouldQuerySwimmingPool =
            addressSelected &&
            gnafSelected &&
            (addressParsed?.state === "NSW" || addressParsed?.state === "QLD") &&
            SWIMMING_POOL_PROPERTY_TYPES.includes(propertyTypeSelected);

        // Exit early if preconditions aren't met or already fetched
        if (shouldQuerySwimmingPool) {
            const fetchSwimmingPoolData = async () => {
                try {
                    const query = { gnaf: gnafSelected, address: addressSelected, state: addressParsed.state };
                    console.log("swimming pool query:", query);
                    const report = await getSwimmingPoolInfo(tokens?.idToken, query);
                    setSwimmingPoolData(report);
                    console.log("swimming pool:", report);
                } catch (error) {
                    console.error("Error fetching swimming pool info:", error);
                }
            };
            fetchSwimmingPoolData();
        }

        if (gnafSelected && propertyTypeSelected && addressParsed) {
            const query = {
                property_type: propertyTypeSelected,
                ...Object.fromEntries(Object.entries(addressParsed).filter(([, v]) => v))
            };
            console.log(query);
            const queryReport = async () => {
                try {
                    setIsLoading(true);
                    const report = await getReport(tokens?.idToken, query);
                    setReportData(report);
                } catch (error) {
                    console.error('Failed to fetch report:', error);
                    setIsLoading(false);
                    setReportData(null);
                }
            };
            queryReport();
        };

    }, [propertyTypeSelected]);

    useEffect(() => {
        if (reportData) {
            // console.log("useEffect", reportData);
            setMappedData([]);
            setDealAcceptance(reportData?.metrics ? reportData?.metrics['Final Result'] : 'N/A');
            let dataList = [];
            if (reportData?.metrics) {
                Object.entries(reportData?.metrics).map((i) => {
                    if (typeof (i[1]) != 'string') {
                        dataList.push(
                            {
                                'name': i[0],
                                'results': i[1],
                            }
                        );
                    }
                });
                setMappedData(dataList);
                setReportVisibility(true);
                setImageList(processImageList(reportData));
                hasFetchedRef.current = true;
            }
            // Always set loading to false when reportData is processed
            setIsLoading(false);
        } else if (reportData === null) {
            // Handle explicit null (error case)
            setIsLoading(false);
            setReportVisibility(false);
        }
    }, [reportData]);

    useEffect(()=>{
        console.log("addressParsed",addressParsed)
        console.log('panels',panels)

    },[addressParsed])
    // Update panel data when report data changes
    useEffect(() => {
        if (reportData && gnafSelected) {
            const isNewAddress = panelValues?.gnaf !== gnafSelected;
            
            const newSecurityData = {
                'gnaf': gnafSelected,
                'property_type': propertyTypeSelected,
                'postcode': addressParsed?.postcode,
                'final_result': reportData?.metrics ? reportData?.metrics['Final Result'] : 'N/A',
                'storage': {
                    'gnafSelected': gnafSelected ?? '',
                    'propertyTypeSelected': propertyTypeSelected ?? '',
                    'addressSelected': addressSelected ?? '',
                    'addressParsed': addressParsed ?? {},
                    'reportData': reportData ?? {},
                    'hasFetchedRef': hasFetchedRef?.current ?? false
                }
            };

            // For new addresses, populate with report data. For existing addresses, preserve user edits.
            if (isNewAddress) {
                Object.assign(newSecurityData, {
                    'bedrooms': reportData?.property_summary?.bedrooms,
                    'bathrooms': reportData?.property_summary?.bathrooms,
                    'carparks': reportData?.property_summary?.carparks,
                    'land_area': reportData?.property_summary?.land_area,
                    'floor_area': reportData?.property_summary?.floor_area,
                    'max_lvr': reportData?.max_lvr,
                    'tc_rank': '1RM',
                    'loan_term': '',
                    'market_value': null,
                    'lvr': null,
                    'loan_amount': null,
                    'property_usage': '--',
                    // 'borrower': ''
                });
            } else {
                // Preserve existing values but update max_lvr and final_result
                Object.assign(newSecurityData, {
                    'max_lvr': reportData?.max_lvr
                });
            }

            updatePanelValues(newSecurityData);
        }
    }, [reportData, gnafSelected, propertyTypeSelected, addressParsed]);


    const styles = StyleSheet.create({
        container: {
            flex: 1,
            padding: 'middle',
        },
    });

    const clearReport = () => {
        setPropertyTypeSelected('');
        setReportVisibility(false);
        setAddressSelected('');
        setReportData();
        setMappedData([]);
        setImageList([]);
        setSwimmingPoolData();
        hasFetchedRef.current = false;
    }
    return (
        <AccordionPanel style={{ border: 'none' }} key={id} label={
            <AddressSection reportData={reportData} addressSelected={addressSelected || `Security ${index + 1}`} colorMap={COLOUR_MAPS} isMobile={isMobile} />
        }>
            <GapBox />
            <PrintHideBox zIndex={3}>
                <SearchSection
                    propertyTypeSelected={propertyTypeSelected}
                    setPropertyTypeSelected={setPropertyTypeSelected}
                    addressSelected={addressSelected}
                    setAddressSelected={setAddressSelected}
                    gnafSelected={gnafSelected}
                    setGnafSelected={setGnafSelected}
                    addressParsed={addressParsed}
                    setAddressParsed={setAddressParsed}
                    clearReport={clearReport}
                    isMobile={isMobile}
                    hasFetchedRef={hasFetchedRef}
                    panels={panels} // Changed from inputValues
                />
            </PrintHideBox>
            {isLoading && (<p style={{ zIndex: 0 }}>Loading...</p>)}
            {reportVisibility && !isLoading && (
                <>
                    <Box>
                        <ZoningSection reportData={reportData} colorMap={COLOUR_MAPS} />
                        {isMobile ? (
                            <>
                                <GallerySection imageList={imageList} />
                                <GapBox />

                                {reportData?.links && (
                                    <PrintHideBox zIndex={3}>
                                        <LinksSection reportData={reportData} isMobile={isMobile} />
                                    </PrintHideBox>
                                )}
                            </>
                        )
                            : (<Grid columns={['80%', '20%']} width={{ max: '52rem' }} gap={{ column: '0' }}>
                                <GallerySection imageList={imageList} />
                                {reportData?.links && (
                                    <PrintHideBox zIndex={3}>
                                        <LinksSection reportData={reportData} isMobile={isMobile} />
                                    </PrintHideBox>
                                )}
                            </Grid>)}
                        <GapBox />
                        <InfoSection
                            id={id}
                            isMobile={isMobile}
                            styles={styles}
                            panel={panel}
                            updatePanel={updatePanel}
                            pricingData={pricingData}
                            setPricingData={setPricingData}
                            dealAcceptance={dealAcceptance}
                            propertyTypeSelected={propertyTypeSelected}
                            reportData={reportData}
                            addressSelected={addressSelected}
                            mappedData={mappedData}
                            swimmingPoolData={swimmingPoolData}
                        />


                        {/* <MetricsSection mappedData={mappedData} /> */}

                        {/* {!isLoading && (<PoolRegistrationSection swimmingPoolData={swimmingPoolData} />)} */}
                        {/*<p style={{ zIndex: 0 }}>balabala</p>*/}
                    </Box>

                </>)}
            <PrimaryButton alignSelf={'start'} label='Delete&nbsp;&nbsp;' onClick={deletePanel} />
        </AccordionPanel>
    )
}

export { SecuritySection }